import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, PhoneCall, Loader2, Bot, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useCall } from '../contexts/CallContext';
import { useModalZIndex } from '../contexts/ModalStackContext';
import { useAuth } from '../hooks/useAuth';

interface CallbackRow {
  id: string;
  business_id: string;
  customer_id: string | null;
  opportunity_id: number | null;
  from_number: string;
  name: string | null;
  reason: string | null;
  collected: Record<string, unknown> | null;
  summary: string | null;
  status: 'pending' | 'completed' | 'dismissed';
  viewed: boolean;
  created_at: string;
}

interface CallbackDetailsModalProps {
  /** agent_callbacks.id — null closes the modal. */
  callbackId: string | null;
  onClose: () => void;
  /** Optional: open the linked CRM lead (shown only when the callback has one). */
  onViewLead?: (opportunityId: number) => void;
}

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  const last10 = digits.slice(-10);
  if (last10.length === 10) {
    return `(${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`;
  }
  return raw;
}

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

const STATUS_PILL: Record<CallbackRow['status'], { label: string; cls: string }> = {
  pending: { label: 'Needs call back', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  completed: { label: 'Call back completed', cls: 'bg-green-100 text-green-700 border-green-200' },
  dismissed: { label: 'Dismissed', cls: 'bg-gray-100 text-gray-600 border-gray-200' },
};

/**
 * The single place a callback is acted on. Every surface that shows a
 * callback (Messages, control centre, CRM lead) opens this modal first;
 * the call itself starts from the button here. Opening marks the callback
 * viewed, which clears its unread state everywhere via realtime.
 */
export default function CallbackDetailsModal({
  callbackId,
  onClose,
  onViewLead,
}: CallbackDetailsModalProps) {
  const { startCall, deviceStatus } = useCall();
  const { user } = useAuth();
  const stackZ = useModalZIndex(100010);
  const isOpen = !!callbackId;

  const [callback, setCallback] = useState<CallbackRow | null>(null);
  const [customer, setCustomer] = useState<{ id: string; first_name: string | null; last_name: string | null } | null>(null);
  const [loading, setLoading] = useState(false);
  const [calling, setCalling] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [callError, setCallError] = useState<string | null>(null);

  useEffect(() => {
    if (!callbackId) {
      setCallback(null);
      setCustomer(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data, error } = await supabase
          .from('agent_callbacks')
          .select('*')
          .eq('id', callbackId)
          .maybeSingle();
        if (error) throw error;
        if (cancelled || !data) return;
        const row = data as CallbackRow;
        setCallback(row);

        if (row.customer_id) {
          const { data: cust } = await supabase
            .from('customers')
            .select('id, first_name, last_name')
            .eq('id', row.customer_id)
            .maybeSingle();
          if (!cancelled && cust) setCustomer(cust);
        }

        // Opening the notification counts as seeing it — clear the unread.
        if (!row.viewed) {
          void supabase.from('agent_callbacks').update({ viewed: true }).eq('id', row.id)
            .then(() => broadcastCallbackChange({ id: row.id, viewed: true }));
        }
      } catch (err) {
        console.error('Failed to load callback details:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [callbackId]);

  useEffect(() => {
    if (isOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const displayName =
    callback?.name?.trim()
    || `${customer?.first_name || ''} ${customer?.last_name || ''}`.trim()
    || (callback ? formatPhone(callback.from_number) : '');

  const collectedEntries = callback?.collected
    ? Object.entries(callback.collected).filter(([, v]) => v !== null && v !== undefined && `${v}`.trim() !== '')
    : [];

  // Dials immediately through the call context — the global in-call screen
  // takes over. Never opens the dialler keypad.
  const handleCall = async () => {
    if (!callback || calling) return;
    if (deviceStatus !== 'ready') {
      setCallError('Phone is still connecting — try again in a second.');
      return;
    }
    setCalling(true);
    setCallError(null);
    try {
      await startCall(
        callback.from_number,
        customer
          ? { id: customer.id, first_name: customer.first_name || undefined, last_name: customer.last_name || undefined }
          : undefined,
      );
      // The return call was placed — that's the callback handled, whether or
      // not the customer picks up. (The call-status webhook does this too,
      // but completing here is instant and doesn't depend on webhook timing.)
      void supabase
        .from('agent_callbacks')
        .update({ status: 'completed', completed_at: new Date().toISOString(), viewed: true })
        .eq('id', callback.id)
        .eq('status', 'pending')
        .then(() => broadcastCallbackChange({ id: callback.id, status: 'completed', viewed: true }));
      onClose();
    } catch (err) {
      console.error('Failed to start callback call:', err);
      setCallError('Could not start the call. Please try again.');
    } finally {
      setCalling(false);
    }
  };

  // "I'm done with this" without placing a call — completes the callback so
  // it clears from Actions Required and unread everywhere.
  const handleMarkDone = async () => {
    if (!callback || completing) return;
    setCompleting(true);
    setCallError(null);
    try {
      const { error } = await supabase
        .from('agent_callbacks')
        .update({
          status: 'completed',
          completed_at: new Date().toISOString(),
          completed_by: user?.id || null,
          viewed: true,
        })
        .eq('id', callback.id)
        .eq('status', 'pending');
      if (error) throw error;
      onClose();
    } catch (err) {
      console.error('Failed to mark callback as done:', err);
      setCallError('Could not mark it as done. Please try again.');
    } finally {
      setCompleting(false);
    }
  };

  const pill = callback ? STATUS_PILL[callback.status] || STATUS_PILL.pending : null;

  const modalContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: stackZ, isolation: 'isolate' }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden flex flex-col max-h-[85vh]"
        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200/60 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-[10px] bg-teal-600 flex items-center justify-center flex-shrink-0">
              <PhoneCall className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>
                Callback request
              </h2>
              <p className="text-[13px] text-gray-500 truncate flex items-center gap-1">
                <Bot className="h-3.5 w-3.5" />
                Taken by the AI receptionist{callback ? ` · ${formatWhen(callback.created_at)}` : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto">
          {loading || !callback ? (
            <div className="py-10 flex justify-center">
              <Loader2 className="h-6 w-6 text-teal-600 animate-spin" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[17px] font-bold text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>
                    {displayName}
                  </div>
                  <div className="text-[15px] text-gray-600">{formatPhone(callback.from_number)}</div>
                </div>
                {pill && (
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border flex-shrink-0 ${pill.cls}`}>
                    {pill.label}
                  </span>
                )}
              </div>

              {callback.reason && (
                <div className="p-3 rounded-[12px] bg-teal-50/60 border border-teal-100">
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-teal-700 mb-0.5">
                    Reason for callback
                  </div>
                  <div className="text-[15px] text-gray-900 leading-snug">{callback.reason}</div>
                </div>
              )}

              {collectedEntries.length > 0 && (
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                    Details collected
                  </div>
                  <div className="rounded-[12px] border border-gray-200 divide-y divide-gray-100">
                    {collectedEntries.map(([key, value]) => (
                      <div key={key} className="flex items-baseline gap-3 px-3 py-2">
                        <span className="text-[13px] text-gray-500 capitalize flex-shrink-0">{key}</span>
                        <span className="text-[14px] font-medium text-gray-900 ml-auto text-right break-words min-w-0">
                          {String(value)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {callback.summary && (
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 mb-1.5">
                    Call summary
                  </div>
                  <p className="text-[14px] text-gray-700 leading-relaxed">{callback.summary}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        {callback && (
          <div className="px-5 py-4 border-t border-gray-200/60 shrink-0 space-y-2">
            {callError && (
              <p className="text-[13px] text-red-600 text-center">{callError}</p>
            )}
            <button
              onClick={handleCall}
              disabled={calling}
              className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white px-4 py-3 rounded-[12px] text-[15px] font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              {calling ? <Loader2 className="h-5 w-5 animate-spin" /> : <PhoneCall className="h-5 w-5" />}
              {calling ? 'Calling…' : `Call ${displayName}`}
            </button>
            {callback.status === 'pending' && (
              <button
                onClick={handleMarkDone}
                disabled={completing}
                className="w-full px-4 py-2.5 rounded-[12px] text-sm font-semibold text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
              >
                {completing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Mark as done — no call needed
              </button>
            )}
            {callback.opportunity_id != null && onViewLead && (
              <button
                onClick={() => {
                  onViewLead(callback.opportunity_id as number);
                  onClose();
                }}
                className="w-full px-4 py-2.5 rounded-[12px] text-sm font-semibold text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                View lead in CRM
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
